// MIJOZ KABINETI paroli — `xsib-selfcare` bilan kontrakt.
// Manba: `selfcare/auth/resource/AdminCredentialController.java`.
//
// =====================================================================
// ⛔ BU RADIUS PAROLI EMAS — VA ALOHIDA FAYLDA TURISHINING SABABI SHU
// =====================================================================
// Tizimda abonentning IKKI paroli bor va ular butunlay mustaqil:
//
//   · INTERNET paroli — `subscriber.subscriber.password`, RADIUS/PPPoE
//     autentifikatsiyasi. `subscriberApi.ts` dagi `setPassword()`,
//     `PUT /api/subscribers/{id}/password`.
//   · KABINET paroli — `selfcare.credential`, mijozning shaxsiy
//     kabinetiga kirish. Shu fayl.
//
// Ularni bitta modulga qo'shish «parolni almashtirdim, lekin internet
// ishlamay qoldi» (yoki aksincha) degan holatga olib kelardi va
// nosozlik faqat MIJOZ qo'ng'iroq qilganda bilinardi. Fayl chegarasi —
// eng arzon ajratgich: chaqiruv joyida qaysi parol ekani import
// qatoridan ko'rinadi.
//
// ⚠️ HUQUQ YANGI EMAS: ikkala endpoint ham `subscriber.password.set`
// so'raydi va u `PartnerKind.RESELLER` to'plamida BOR (o'lchandi:
// `PartnerKind.java`). Doira serverda qayta tekshiriladi
// (`AdminCredentialController#requireInScope` → `canSetPassword`) va
// begona abonent uchun `404` qaytadi, `403` EMAS — «yo'q» va
// «ko'rinmaydi» ataylab ajratilmagan (`QARORLAR.md` §5.3).
//
// ⚠️ AGENT (kassir) bu amalni KO'RMAYDI: `subscriber.password.set`
// uning to'plamida yo'q va «Abonentlar» ekrani ham unda ochilmaydi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'

/**
 * `CredentialServiceImpl.MIN_PASSWORD_LENGTH` — server tekshiruvining
 * AYNAN o'zi.
 *
 * ⚠️ TAKROR ATAYLAB (`LOGIN_PATTERN` bilan bir xil sabab): server rad
 * javobi modalning tepasida chiqadi va operator qaysi maydonda xato
 * borligini qidiradi. Bu yerda tekshiruv maydonning ostida, yozilayotgan
 * paytda ko'rinadi.
 */
export const SELFCARE_PASSWORD_MIN = 6

/**
 * `PasswordSetRequest` dagi `@Size(max = 72)` — CVE-2025-22228.
 *
 * ⚠️ BCrypt parolning faqat dastlabki 72 BAYTINI hisobga oladi, ya'ni
 * undan uzun parol foydalanuvchiga QALBAKI xavfsizlik hissi berardi.
 * Bu yerdagi son BELGI sanaydi (`maxLength`), server esa bayt bo'yicha
 * ham tekshiradi — kirillcha parolda ular ajraladi va oxirgi so'z
 * serverniki.
 */
export const SELFCARE_PASSWORD_MAX = 72

const passwordStatusSchema = z.object({
  set: z.boolean(),
  updatedAt: z.string().nullish(),
})

export interface SelfcarePasswordStatus {
  /** Kabinet paroli o'rnatilganmi */
  set: boolean
  /** Oxirgi marta qachon o'rnatilgani (ISO), yoki `null` */
  updatedAt: string | null
}

/**
 * Kabinet paroli o'rnatilganmi va qachon.
 *
 * ⚠️ PAROLNING O'ZI QAYTMAYDI va qaytishi MUMKIN EMAS: backend faqat
 * xesh saqlaydi (`selfcare.credential.password_hash`), o'qish endpointi
 * esa umuman yo'q. Bu yerda kelayotgani — «bormi va qachon», boshqa
 * hech narsa.
 *
 * ⚠️ `set: false` XATO EMAS, HOLAT va u operatorga aytiladi: mijoz
 * kabinetga kira olmasligining sababi aynan shu bo'lishi mumkin.
 * Shuning uchun bu funksiya xatoni YUTMAYDI — 404 (begona abonent) va
 * 500 «parol yo'q» bo'lib ko'rinsa, operator mavjud parolni bekorga
 * almashtirardi.
 */
export async function fetchSelfcarePasswordStatus(
  subscriberId: string,
  signal: AbortSignal,
): Promise<SelfcarePasswordStatus> {
  const response = await api.get<unknown>(
    `/api/selfcare-admin/subscribers/${subscriberId}/selfcare-password`,
    { signal },
  )
  const parsed = passwordStatusSchema.parse(response.data)
  return { set: parsed.set, updatedAt: parsed.updatedAt ?? null }
}

/**
 * Kabinet parolini o'rnatadi (yoki almashtiradi).
 *
 * ⚠️ Abonent bu parolni HALI BILMAYDI — vaucher/SMS OTP infratuzilmasi
 * hozircha yo'q, ya'ni birinchi parolni kimdir tashqaridan qo'yishi
 * shart. Abonent keyin uni kabinetdan o'zi almashtiradi
 * (`PUT /api/selfcare/me/password`).
 *
 * ⚠️ Javobda parol YO'Q — faqat `OperationResult`. Panel ham uni
 * ekranga qaytarmaydi (`CLAUDE.md` §7).
 */
export async function setSelfcarePassword(
  subscriberId: string,
  password: string,
): Promise<OperationResult> {
  const response = await api.post<unknown>(
    `/api/selfcare-admin/subscribers/${subscriberId}/selfcare-password`,
    { password },
  )
  return operationResultSchema.parse(response.data)
}
